// ✅ 'validate' predefined process — visualizes per-field validation result (green ok / red not ok + hover details).
// Backend: app/backend/kgnf_inv_post.py (_validate_field_core) writes `field_validations[fieldid]` on invoice / invoice item.

import React from "react";
import { TooltipHost, DirectionalHint } from "@fluentui/react";
import styles from "./ValidateFieldWrap.module.css";

export interface gfieldvalidation {
    is_validate?: boolean;
    validate_result?: "ok" | "not ok" | string;
    validate_source_vals?: string[];
    validate_source_count?: number;
    validate_invoice_vals?: string[];
    validate_unmatched_vals?: string[];
    filter_condition?: string;
    filter_column?: string;
    invoice_value?: string;
}

const conditionLabels: Record<string, string> = {
    eq: "Equals",
    gt: "Source greater than invoice value",
    gteq: "Source greater than or equal to invoice value",
    lt: "Source less than invoice value",
    lteq: "Source less than or equal to invoice value"
};

// returns validation info for a field only if it is a 'validate' field
export const getFieldValidation = (data: any, fieldId: string): gfieldvalidation | null => {
    const v = data?.field_validations?.[fieldId];
    if (!v || typeof v !== "object" || v.is_validate !== true) return null;
    return v as gfieldvalidation;
};

const joinVals = (vals?: string[]) => (Array.isArray(vals) && vals.length > 0 ? vals.join(", ") : "—");

const ValidationDetails: React.FC<{ v: gfieldvalidation }> = ({ v }) => {
    const isOk = v.validate_result === "ok";
    const srcCount = v.validate_source_count ?? v.validate_source_vals?.length ?? 0;
    const srcShown = v.validate_source_vals?.length ?? 0;
    return (
        <div className={styles.validateTooltip}>
            <div className={isOk ? styles.validateTitleOk : styles.validateTitleNotOk}>Validation: {isOk ? "OK" : "Not OK"}</div>
            <div className={styles.validateRow}>
                <span className={styles.validateLabel}>Condition</span>
                <span>{conditionLabels[v.filter_condition || "eq"] || v.filter_condition}</span>
            </div>
            <div className={styles.validateRow}>
                <span className={styles.validateLabel}>Source column</span>
                <span>{v.filter_column || "—"}</span>
            </div>
            <div className={styles.validateRow}>
                <span className={styles.validateLabel}>Invoice values</span>
                <span>{joinVals(v.validate_invoice_vals)}</span>
            </div>
            <div className={styles.validateRow}>
                <span className={styles.validateLabel}>Source values</span>
                <span>
                    {joinVals(v.validate_source_vals)}
                    {srcCount > srcShown ? ` (+${srcCount - srcShown} more)` : ""}
                </span>
            </div>
            {(v.validate_unmatched_vals?.length ?? 0) > 0 && (
                <div className={styles.validateRow}>
                    <span className={styles.validateLabel}>Invoice values not matched</span>
                    <span>{joinVals(v.validate_unmatched_vals)}</span>
                </div>
            )}
        </div>
    );
};

interface ValidateFieldWrapProps {
    validation: gfieldvalidation | null;
    children: React.ReactNode;
}

// pass-through when the field is not a 'validate' field
const ValidateFieldWrap: React.FC<ValidateFieldWrapProps> = ({ validation, children }) => {
    if (!validation) return <>{children}</>;
    const isOk = validation.validate_result === "ok";
    return (
        <TooltipHost
            content={<ValidationDetails v={validation} />}
            directionalHint={DirectionalHint.topCenter}
            styles={{ root: { display: "block", width: "100%" } }}
        >
            <div className={isOk ? styles.validateOk : styles.validateNotOk}>{children}</div>
        </TooltipHost>
    );
};

export default ValidateFieldWrap;
